import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  FileText, 
  Users, 
  Settings, 
  Search,
  LogOut,
  User,
  FileStack,
  Home,
  TrendingUp,
  Building2,
  CalendarCheck,
  HelpCircle,
  LayoutDashboard
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, Form } from 'react-router'
import { ThemeSwitch } from '#app/routes/resources+/theme-switch.tsx'
import { cn, getUserImgSrc } from '#app/utils/misc.tsx'
import { useUser } from '#app/utils/user.ts'
import { LanguageSwitcherSidebar } from './language-switcher-sidebar.tsx'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar.tsx'
import { Button } from './ui/button.tsx'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip.tsx'

interface SidebarProps {
  children?: React.ReactNode
  recentQuotes?: Array<{
    id: string
    companyName: string
    createdAt: string
  }>
  onSearchClick?: () => void
}

export function Sidebar({ children, recentQuotes = [], onSearchClick }: SidebarProps) {
  const { t } = useTranslation('app')
  const [isCollapsed, setIsCollapsed] = useState(false)
  const location = useLocation()
  const user = useUser()

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed)
  }

  // Close all tooltips when sidebar state changes
  useEffect(() => {
    // Force all tooltips to close by triggering a re-render
    const tooltips = document.querySelectorAll('[data-radix-tooltip-content]')
    tooltips.forEach(tooltip => {
      tooltip.remove()
    })
  }, [isCollapsed])

  const mainMenuItems = [
    {
      icon: LayoutDashboard,
      label: t('navigation.dashboard'),
      href: '/dashboard',
      active: location.pathname === '/dashboard'
    },
    {
      icon: Plus,
      label: t('sidebar.newQuote'),
      href: '/quotes/new',
      active: location.pathname === '/quotes/new'
    },
    {
      icon: FileText,
      label: t('sidebar.myQuotes'),
      href: '/quotes',
      active: location.pathname === '/quotes' && location.search === ''
    },
    {
      icon: TrendingUp,
      label: t('navigation.deals'),
      href: '/deals',
      active: location.pathname.startsWith('/deals')
    },
    {
      icon: Building2,
      label: t('navigation.organizations'),
      href: '/organizations',
      active: location.pathname.startsWith('/organizations')
    },
    {
      icon: Users,
      label: t('navigation.contacts'),
      href: '/contacts',
      active: location.pathname.startsWith('/contacts')
    },
    {
      icon: CalendarCheck,
      label: t('navigation.activities'),
      href: '/activities',
      active: location.pathname.startsWith('/activities')
    },
    {
      icon: FileStack,
      label: t('sidebar.quoteDesigns'),
      href: '/templates/designer',
      active: location.pathname.startsWith('/templates')
    },
    {
      icon: HelpCircle,
      label: t('navigation.help'),
      href: '/help',
      active: location.pathname.startsWith('/help')
    },
  ]

  const bottomMenuItems = [
    {
      icon: User,
      label: t('sidebar.profile'),
      href: `/users/${user.username}`,
      active: location.pathname === `/users/${user.username}`
    },
    {
      icon: Settings,
      label: t('sidebar.settings'),
      href: '/settings/profile',
      active: location.pathname.startsWith('/settings')
    },
  ]

  return (
    <TooltipProvider key={isCollapsed ? 'collapsed' : 'expanded'} delayDuration={300}>
      <div className="flex min-h-screen">
        <div className={cn(
          "relative flex flex-col border-r bg-background transition-all duration-300 h-screen sticky top-0 flex-shrink-0 z-40",
          isCollapsed ? "w-16" : "w-64"
        )}>
          {/* Header with collapse button */}
          <div className="flex h-14 items-center justify-between px-4 border-b flex-shrink-0">
            {!isCollapsed && (
              <Link to="/" className="flex items-center gap-2">
                <span className="font-semibold text-lg font-[family-name:var(--font-heading)] dark:text-white">Offerty</span>
              </Link>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className={cn("h-8 w-8", isCollapsed && "mx-auto")}
            >
              {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>

          {/* Search button */}
          <div className="px-3 py-3">
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start gap-2",
                isCollapsed && "px-0 justify-center"
              )}
              onClick={onSearchClick}
            >
              <Search className="h-4 w-4" />
              {!isCollapsed && (
                <>
                  <span className="text-sm text-muted-foreground">{t('sidebar.search')}</span>
                  <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                    <span className="text-xs">⌘</span>F
                  </kbd>
                </>
              )}
            </Button>
          </div>

          {/* Main menu */}
          <nav className="flex-1 space-y-1 px-3 overflow-y-auto">
            {mainMenuItems.map((item) => (
              <Tooltip key={item.href} delayDuration={0}>
                <TooltipTrigger asChild>
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent",
                      item.active && "bg-accent",
                      isCollapsed && "justify-center px-0"
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {!isCollapsed && <span>{item.label}</span>}
                  </Link>
                </TooltipTrigger>
                {isCollapsed && (
                  <TooltipContent side="right">
                    <p>{item.label}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            ))}
          </nav>

          {/* Recent quotes section */}
          {!isCollapsed && recentQuotes.length > 0 && (
            <div className="px-3 pt-3">
              <h3 className="mb-2 px-3 text-sm font-medium font-[family-name:var(--font-heading)] text-muted-foreground">
                {t('sidebar.recentQuotes')}
              </h3>
              <nav className="space-y-1">
                {recentQuotes.map((quote) => (
                  <Link
                    key={quote.id}
                    to={`/quotes/${quote.id}`}
                    className={cn(
                      "flex items-center rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent",
                      location.pathname === `/quotes/${quote.id}` && "bg-accent"
                    )}
                  >
                    <span className="truncate">{quote.companyName}</span>
                  </Link>
                ))}
              </nav>
            </div>
          )}

          {/* User section */}
          <div className="border-t">
            <div className="space-y-1 p-3">
              {bottomMenuItems.map((item) => (
                <Tooltip key={item.href} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent",
                        item.active && "bg-accent",
                        isCollapsed && "justify-center px-0"
                      )}
                    >
                      <item.icon className="h-4 w-4" />
                      {!isCollapsed && <span>{item.label}</span>}
                    </Link>
                  </TooltipTrigger>
                  {isCollapsed && (
                    <TooltipContent side="right">
                      <p>{item.label}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              ))}
              
              {/* Theme toggle */}
              <div className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2",
                isCollapsed && "justify-center px-0"
              )}>
                <ThemeSwitch />
                {!isCollapsed && <span className="text-sm">{t('sidebar.theme')}</span>}
              </div>
              
              {/* Language switcher */}
              <LanguageSwitcherSidebar isCollapsed={isCollapsed} />
              
              {/* Logout */}
              <Form action="/logout" method="POST">
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      type="submit"
                      variant="ghost"
                      className={cn(
                        "w-full justify-start gap-3",
                        isCollapsed && "justify-center px-0"
                      )}
                    >
                      <LogOut className="h-4 w-4" />
                      {!isCollapsed && <span>{t('sidebar.logout')}</span>}
                    </Button>
                  </TooltipTrigger>
                  {isCollapsed && (
                    <TooltipContent side="right">
                      <p>{t('sidebar.logout')}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </Form>
              
              {/* User info */}
              {!isCollapsed && (
                <div className="flex items-center gap-3 rounded-lg px-3 py-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={getUserImgSrc(user.image?.objectKey)} />
                    <AvatarFallback>{user.name?.charAt(0) || user.username.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{user.name || user.username}</span>
                    <span className="text-xs text-muted-foreground">@{user.username}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {children}
        </div>
      </div>
    </TooltipProvider>
  )
}